// In: apps/web/src/analysis/sections/general/finance/tables.ts
// REPLACE the entire file:

import { AlsoStoreFn, createMaybeAlsoStoreFn } from '../../../utils/store-helpers'
import { shortenCurrency } from '../../../utils/chart-formatters'
import { formatCurrency as formatCurrencyUtil, formatMachineYearMonth } from '../../../utils/string-formatters'
import { calculateAllPnlMetrics } from '../../../utils/pnl'
import { currentUser } from '@/core/state'
import { db } from '@/core/firebase'
import { doc, getDoc } from 'firebase/firestore'
import { showPnlTargetModal } from '@/data-hub/modals'
import paths from '@/db/paths'
import { branchContextService } from '@/services/branchContextService'

interface PnlReport { period: string, pnlData?: Record<string, Record<string, number>> }

/**
 * Generates a P&L Target vs Actual comparison table for a single period.
 * [MODIFIED] Uses expanded Global Brand Targets (Main Categories).
 */
export async function generatePnlTargetComparisonTable(
  period: string, 
  branch: string, 
  containerId: string, 
  config?: { 
    alsoStore?: AlsoStoreFn, 
    branchId?: string,
    brandId?: string 
  }
) {
  const container = document.getElementById(containerId)
  if (!container || !currentUser) return
  container.innerHTML = '<p class="text-gray-500">Loading P&L comparison...</p>'

  const reportId = `${period}_${branch.replace(/\s+/g, '_')}`
  const branchId = config?.branchId || branchContextService.getBranchIdByName(branch)
  
  if (!branchId) {
    container.innerHTML = `<div class="text-center p-4 border rounded-lg bg-gray-50"><p class="text-gray-500">Branch information missing.</p></div>`
    return
  }

  // 1. Try Branch Default Targets (Configuration)
  try {
    const branchDocRef = doc(db, paths.branches(branchId).path)
    const branchSnap = await getDoc(branchDocRef)

    if (branchSnap.exists()) {
        const branchData = branchSnap.data()
        const t = branchData.targets?.pnl

        // Check if ANY target is set (even just one implies configuration exists)
        if (t) {
            
            const pnlReportRef = doc(db, paths.branches(branchId).pnlReports(reportId).path)
            const pnlReportSnap = await getDoc(pnlReportRef)

            if (pnlReportSnap.exists()) {
                const pnlData = pnlReportSnap.data().pnlData
                const metrics = calculateAllPnlMetrics(pnlData || {})
                const revenue = metrics['Pendapatan (Revenue)'] || 0

                // Construct synthetic target object
                const syntheticTarget = {
                    period: period,
                    targetRevenue: revenue, 
                    targets: {} as Record<string, number>
                }

                // --- [THIS IS THE FIX] ---
                // Map the new Main Category inputs to the chart keys
                syntheticTarget.targets['Pendapatan (Revenue)'] = revenue; // 100% baseline
                
                if (t.cogsPercent) syntheticTarget.targets['Harga Pokok Produksi'] = revenue * (t.cogsPercent / 100);
                
                if (t.opexPercent) syntheticTarget.targets['Beban Operasional (OPEX)'] = revenue * (t.opexPercent / 100);
                // (Wages/Rent removed from main calculation if user sets OPEX directly)

                if (t.nonOpexPercent) syntheticTarget.targets['Beban Non Operasional'] = revenue * (t.nonOpexPercent / 100);
                
                if (t.depreciationPercent) syntheticTarget.targets['Depresiasi/ Amortisasi'] = revenue * (t.depreciationPercent / 100);
                
                if (t.interestPercent) syntheticTarget.targets['Bunga'] = revenue * (t.interestPercent / 100);
                
                if (t.taxPercent) syntheticTarget.targets['Pajak (PB1)'] = revenue * (t.taxPercent / 100);

                // Auto-calculate Profits for the target column?
                // showPnlTargetModal handles calculations for Achievement %, 
                // but we need to feed it the absolute target values for the rows to show up.
                
                // Gross Profit Target
                if (t.cogsPercent) {
                   const gp = revenue - (revenue * (t.cogsPercent / 100));
                   syntheticTarget.targets['Laba Kotor (Gross Profit)'] = gp;
                }
                
                // Net Operating Income Target
                if (t.cogsPercent && t.opexPercent) {
                    const gp = revenue - (revenue * (t.cogsPercent / 100));
                    const noi = gp - (revenue * (t.opexPercent / 100));
                    syntheticTarget.targets['Pendapatan Bersih Operasional (Net Operating Income)'] = noi;
                }

                // EBITDA Target
                if (t.cogsPercent && t.opexPercent && t.nonOpexPercent) {
                    const gp = revenue - (revenue * (t.cogsPercent / 100));
                    const noi = gp - (revenue * (t.opexPercent / 100));
                    const ebitda = noi - (revenue * (t.nonOpexPercent / 100));
                    syntheticTarget.targets['Pendapatan Bersih Sebelum Deprisiasi/Amortisasi, Bunga & Pajak (EBITDA)'] = ebitda;
                }

                // Net Income Target (Approximate, calculated from top down)
                if (t.cogsPercent && t.opexPercent && t.nonOpexPercent) {
                    // Start with EBITDA
                    let ni = revenue * (1 - (t.cogsPercent/100) - (t.opexPercent/100) - (t.nonOpexPercent/100));
                    // Subtract others if they exist
                    if (t.depreciationPercent) ni -= revenue * (t.depreciationPercent/100);
                    if (t.interestPercent) ni -= revenue * (t.interestPercent/100);
                    if (t.taxPercent) ni -= revenue * (t.taxPercent/100);
                    
                    syntheticTarget.targets['Pendapatan Bersih (Net Income)'] = ni;
                }
                // --- [END FIX] ---

                await showPnlTargetModal(syntheticTarget, reportId, { branchId })
                const modalBody = document.getElementById('pnl-target-modal-body')
                if (modalBody) container.innerHTML = modalBody.innerHTML
                document.getElementById('pnl-target-modal')?.classList.add('hidden')
                return
            } else {
                container.innerHTML = `<p class="text-gray-500 text-center p-4">Actual P&L data not found for this period.</p>`
                return
            }
        }
    }
  } catch (e) {
    console.error("Error fetching branch targets:", e);
  }

  // 2. If missing, show the "Set Target" button
  container.innerHTML = `
    <div class="text-center p-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 flex flex-col items-center justify-center h-64">
      <div class="mb-4">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100">
            <svg class="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
        </div>
      </div>
      <h3 class="text-lg font-medium text-gray-900 mb-1">No Brand Targets Found</h3>
      <p class="text-sm text-gray-500 mb-6">Set default targets for this brand to see performance comparisons.</p>
      <button 
        id="btn-add-brand-target" 
        class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        data-brand-id="${config?.brandId || ''}"
      >
        <svg class="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
        </svg>
        Set Brand Target
      </button>
    </div>`
}

/**
 * Generates a historical P&L table with expandable rows for sub-categories.
 */
export function generateHistoricalPnlTable(reports: PnlReport[], theadId: string, tbodyId: string, config?: { alsoStore?: AlsoStoreFn }) {
  const thead = document.getElementById(theadId)
  const tbody = document.getElementById(tbodyId)
  if (!thead || !tbody) return

  if (reports.length === 0) {
    thead.innerHTML = ''
    tbody.innerHTML = '<tr><td colspan="13" class="p-4 text-center text-gray-500">No P&L reports found for this period.</td></tr>'
    return
  }

  const periodHeaders = reports
    .map((r) => `<th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">${new Date(r.period + '-02').toLocaleString('default', { month: 'short', year: 'numeric' })}</th>`)
    .join('')
  thead.innerHTML = `<tr><th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>${periodHeaders}</tr>`

  tbody.innerHTML = ''
  const allMetrics = [
    'Pendapatan (Revenue)',
    'Harga Pokok Produksi',
    'Laba Kotor (Gross Profit)',
    'Beban Operasional (OPEX)',
    'Pendapatan Bersih Operasional (Net Operating Income)',
    'Beban Non Operasional',
    'Pendapatan Bersih Sebelum Deprisiasi/Amortisasi, Bunga & Pajak (EBITDA)',
    'Depresiasi/ Amortisasi',
    'Bunga',
    'Pajak (PB1)',
    'Pendapatan Bersih (Net Income)',
  ]
  const alsoStore = createMaybeAlsoStoreFn(config?.alsoStore)

  allMetrics.forEach((metricName) => {
    const metrics = reports.map((report) => calculateAllPnlMetrics(report.pnlData || {}))
    const isSubtotal = !reports[0]?.pnlData?.[metricName]
    const mainRow = document.createElement('tr')
    let mainRowHtml = ''

    if (!isSubtotal) {
      mainRow.className = 'pnl-main-category bg-gray-50 hover:bg-gray-100 cursor-pointer'
      const sanitizedMetricName = metricName.replace(/[^a-zA-Z0-9]/g, '')
      ;(mainRow as any).dataset.target = `sub-items-of-${sanitizedMetricName}`
      mainRowHtml += `<td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-semibold"><div class="flex items-center">${metricName}<svg class="w-4 h-4 ml-2 transform transition-transform chevron-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg></div></td>`
    } else {
      mainRow.className = 'bg-white font-bold'
      mainRowHtml += `<td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${metricName}</td>`
    }

    metrics.forEach((metricSet, index) => {
      const value = metricSet[metricName] || 0
      alsoStore(value, (v) => ({ historicalPnl: { [formatMachineYearMonth(reports[index]!.period)]: { [metricName]: formatCurrencyUtil(v) } } }))
      mainRowHtml += `<td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right font-mono">${shortenCurrency(value)}</td>`
    })
    mainRow.innerHTML = mainRowHtml
    tbody.appendChild(mainRow)

    if (!isSubtotal) {
      const sanitizedMetricName = metricName.replace(/[^a-zA-Z0-9]/g, '')
      const allSubKeys = new Set(reports.flatMap((r) => Object.keys(r.pnlData?.[metricName] || {})))
      allSubKeys.forEach((subKey) => {
        const subRow = document.createElement('tr')
        subRow.className = `pnl-sub-category sub-items-of-${sanitizedMetricName} hidden`
        let subRowHtml = `<td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 pl-8">${subKey}</td>`
        reports.forEach((report) => {
          const subValue = report.pnlData?.[metricName]?.[subKey] || 0
          alsoStore(subValue, (v) => ({ historicalPnl: { [formatMachineYearMonth(report.period)]: { [metricName]: { [subKey]: formatCurrencyUtil(v) } } } }))
          subRowHtml += `<td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right font-mono">${shortenCurrency(subValue)}</td>`
        })
        subRow.innerHTML = subRowHtml
        tbody.appendChild(subRow)
      })
    }
  })
}

/** Accordion listener for history table */
export function initializePnlAccordionListener(): void {
  document.getElementById('general-pnl-history-tbody')?.addEventListener('click', (e) => {
    const target = e.target as HTMLElement
    const headerRow = target.closest('.pnl-main-category') as HTMLElement | null
    if (headerRow) {
      const targetClass = (headerRow as HTMLElement).dataset.target
      if (!targetClass) return
      const subRows = document.querySelectorAll(`.${targetClass}`)
      const chevron = headerRow.querySelector('.chevron-icon')
      subRows.forEach((row) => row.classList.toggle('hidden'))
      chevron?.classList.toggle('rotate-180')
    }
  })
}